import { type DesignNode, isPointInShape } from '@repo/api-contracts';
import {
  type CreateContext,
  hitTestLayers,
  type MeasureText,
  type PhotoDimensions,
  type RenderMaterials,
  renderComponent,
} from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import {
  Fragment,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type BoxNode,
  clientToComponentMm,
  dragMove,
  dragResize,
  dragResizeEdge,
  dragRotate,
  dragTextCorner,
  dragTextStretch,
  dragTextWidth,
  type FlatFitMode,
  type FlatView,
  findNode,
  type HandleKind,
  handleCanvasKeyDown,
  hitHandle,
  type InlineEditing,
  isEdgeKind,
  nextInlineTextSession,
  rasterPxPerMmOf,
  type SurfaceSlot,
  surfaceBoxOf,
  surfaceViewIn,
  unionRectOf,
  useCanvasViewport,
} from '@/features/design-editor';
import { drawRasterToCanvas } from '@/shared/lib';
import { cn } from '@/shared/ui';
import { AreaShapeOutline } from './area-shape-outline';
import { InlineTextEditor } from './inline-text-editor';
import { RotationFeedback } from './rotation-feedback';
import { SelectionOverlay } from './selection-overlay';
import { TextEditHint } from './text-edit-hint';

/** 캔버스에 올리는 편집 표면 하나 — 면에 배치된 인쇄 영역과 그 레이어. */
export interface EditSurface extends SurfaceSlot {
  readonly layers: readonly DesignNode[];
}

export interface FlatEditCanvasProps {
  /** 이 면의 인쇄 영역 전부 — 한 화면에 같이 보이고 어느 것이든 바로 편집된다. */
  readonly surfaces: readonly EditSurface[];
  /** 공유 공간 크기 — 사진이 있으면 사진 px, 없으면 가상 mm 공간. 자리(rect)는 이 공간 위 좌표다. */
  readonly space: PhotoDimensions;
  /** 지금 편집 중인(텍스트·이미지가 들어가는) 인쇄 영역. 다른 영역을 누르면 onActivate 로 바뀐다. */
  readonly activeComponentId: string;
  readonly onActivate: (componentId: string) => void;
  readonly selectedNodeId: string | null;
  readonly materials: RenderMaterials;
  /** 지금 편집 중인 영역의 안내선 — 선택 중일 때 그 영역 위에만 겹친다. */
  readonly guide: Raster | null;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
  readonly onSelect: (nodeId: string | null) => void;
  readonly onChangeNode: (node: BoxNode) => void;
  readonly onNudge: (dxMm: number, dyMm: number) => void;
  readonly onDeleteSelected: () => void;
  /** 있으면 목업 사진을 공간 전체에 깔고 그 위에 편집 표면들을 겹친다. 없으면 흰 인쇄 영역들만 보인다. */
  readonly backdrop?: { readonly url: string } | null;
  /**
   * 캔버스 위에서 글자를 바로 고칠 때 — 텍스트를 더블클릭하거나 고른 채 Enter 를 누르면 노드 자리에 입력칸이 열리고,
   * 글자가 바뀔 때마다 새 글자와 이번 고치기의 이름(session)을 알린다. 같은 session 은 undo 한 번으로 묶는다. 없으면 열지 않는다.
   */
  readonly onTextInput?: (nodeId: string, text: string, session: string) => void;
  /** 바로 고치기를 열어 달라는 요청(텍스트를 새로 넣었을 때) — seq 가 바뀔 때마다 그 노드의 입력칸을 연다. */
  readonly editRequest?: { readonly nodeId: string; readonly seq: number } | null;
  /** 곡면 상품의 실제 합성(사진 전체 크기) — 있고 끌고 있지 않으면 평면 겹침 대신 이것을 보인다. */
  readonly warped?: ImageBitmap | null;
  /** 곡면 합성 위 클릭(사진 원본 px) → 역워프로 고른 레이어 id. 영역 전환은 호출자가 한다. */
  readonly onWarpedHit?: (photoPx: { x: number; y: number }) => string | null;
  /** 목업 사진을 못 불러왔을 때(서명 URL 만료 등) — 호출자가 새 URL 을 받는다. */
  readonly onBackdropError?: () => void;
  /**
   * 보기 모드 — photo(전체 보기, 자동 맞춤)·area(영역 확대: 인쇄 영역들을 채운 배율에서 시작해 휠로 확대·축소하고
   * 빈 곳을 끌어 옮길 수 있다). 전체 보기에서 휠을 굴리면 영역 확대로 넘어간다(onFitModeChange).
   */
  readonly fitMode?: FlatFitMode;
  readonly onFitModeChange?: (mode: FlatFitMode) => void;
  /** 스펙 대역용 — 기본은 렌더러·캔버스 그리기. */
  readonly renderComponentImpl?: typeof renderComponent;
  readonly drawImpl?: (canvas: HTMLCanvasElement, raster: Raster) => void;
}

interface Drag {
  readonly kind: HandleKind;
  readonly original: BoxNode;
  readonly componentId: string;
  readonly startMm: { xMm: number; yMm: number };
}

interface SelectedBox {
  readonly node: BoxNode;
  readonly componentId: string;
}

interface Picked {
  readonly nodeId: string | null;
  /** 누른 자리의 영역. 곡면 역워프 히트는 영역을 모른다(호출자가 바꾼다). */
  readonly componentId: string | null;
}

/** 표면 전부를 문서 모양으로 — findNode 가 노드와 그 영역을 같이 찾는다. */
const documentOf = (surfaces: readonly EditSurface[]) => ({
  productId: '',
  colorOptionId: '',
  components: surfaces.map((surface) => ({
    componentId: surface.component.id,
    layers: [...surface.layers],
  })),
});

const selectedBoxOf = (surfaces: readonly EditSurface[], id: string | null): SelectedBox | null => {
  if (id === null) return null;
  const found = findNode(documentOf(surfaces), id);
  return found !== null && found.node.type !== 'group'
    ? { node: found.node, componentId: found.componentId }
    : null;
};

const insideComponent = (surface: EditSurface, mm: { xMm: number; yMm: number }) =>
  isPointInShape(surface.component, mm);

/**
 * 평면 편집 캔버스(U6 자체 Canvas 2D). 한 면의 인쇄 영역 전부를 공유 공간(목업 사진) 위 제자리에 겹쳐 그리고,
 * 어느 영역의 레이어든 바로 누르면 선택된다 — 누른 영역이 편집 중인 영역이 된다.
 * 포인터: 핸들 → 크기/회전/폭, 노드 → 이동, 빈 곳 → 선택 해제(다른 영역의 빈 곳이면 그 영역으로 전환),
 * 텍스트 더블클릭 → 그 자리에서 글자 바로 고치기(InlineTextEditor).
 * 키보드: 화살표 1mm(Shift 10), Delete, Esc(선택 해제), 텍스트를 고른 채 Enter → 글자 바로 고치기.
 */
export const FlatEditCanvas = ({
  surfaces,
  space,
  activeComponentId,
  onActivate,
  selectedNodeId,
  materials,
  guide,
  createContext,
  measure,
  onSelect,
  onChangeNode,
  onNudge,
  onDeleteSelected,
  backdrop = null,
  onTextInput,
  editRequest = null,
  onBackdropError,
  warped = null,
  onWarpedHit,
  fitMode = 'photo',
  onFitModeChange,
  renderComponentImpl = renderComponent,
  drawImpl = drawRasterToCanvas,
}: FlatEditCanvasProps): ReactElement => {
  const canvasRefs = useRef(new Map<string, HTMLCanvasElement>());
  const guideRef = useRef<HTMLCanvasElement>(null);
  const warpedRef = useRef<HTMLCanvasElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [live, setLive] = useState<BoxNode | null>(null);
  const [editing, setEditing] = useState<InlineEditing | null>(null);
  /** 열린 입력칸의 '지금 값 올리기'. */
  const flushInline = useRef<(() => void) | null>(null);

  const viewport = useCanvasViewport<Picked>({
    space,
    focus: unionRectOf(surfaces.map((surface) => surface.rect)),
    fitMode,
    onFitModeChange,
  });
  const { hostRef, fit, photoBox, hostRect, capture, release } = viewport;
  const fallbackView: FlatView = { pxPerMmX: 1, pxPerMmY: 1, offsetX: fit.left, offsetY: fit.top };
  const viewOf = (componentId: string): FlatView => {
    const surface = surfaces.find((candidate) => candidate.component.id === componentId);
    return surface === undefined
      ? fallbackView
      : surfaceViewIn(fit, surface.rect, surface.component);
  };
  const surfaceOf = (componentId: string): EditSurface | undefined =>
    surfaces.find((surface) => surface.component.id === componentId);

  const stored = selectedBoxOf(surfaces, selectedNodeId);
  const selected: SelectedBox | null =
    live !== null && drag !== null ? { node: live, componentId: drag.componentId } : stored;
  const selectedView = selected === null ? null : viewOf(selected.componentId);
  // 바로 고치는 중인 텍스트 — 고른 노드가 그 텍스트일 때만(선택이 바뀌거나 노드가 사라지면 닫힌다).
  const editingText =
    editing !== null &&
    onTextInput !== undefined &&
    selected !== null &&
    selected.node.id === editing.nodeId &&
    selected.node.type === 'text'
      ? selected.node
      : null;
  const hiddenId = editingText?.id ?? null;
  const warpedShown = warped !== null && drag === null && backdrop !== null && hiddenId === null;

  // 고르던 텍스트가 바뀌거나 사라지면 고치기를 접는다.
  useEffect(() => {
    if (editing !== null && hiddenId === null) setEditing(null);
  }, [editing, hiddenId]);

  const openTextEditor = (nodeId: string) => {
    if (onTextInput === undefined) return;
    setEditing({ nodeId, session: nextInlineTextSession(nodeId) });
  };

  // 새로 넣은 텍스트 — 바로 고치기를 열어 기본 문구를 고른 채 두므로 입력하면 바로 바뀐다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 요청 순번이 바뀔 때만 연다.
  useEffect(() => {
    if (editRequest !== null) openTextEditor(editRequest.nodeId);
  }, [editRequest?.seq]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 배치는 fit(배율·위치)과 surfaces 로 결정되므로 그 둘로 다시 그린다.
  useEffect(() => {
    const dpr = window.devicePixelRatio || 1;
    for (const surface of surfaces) {
      const canvas = canvasRefs.current.get(surface.component.id);
      if (canvas === undefined) continue;
      const view = surfaceViewIn(fit, surface.rect, surface.component);
      try {
        // 곡면 합성이 보이는 동안은 평면 겹침을 비워 인쇄 영역 점선만 남긴다. 바로 고치는 글자는 입력칸이 대신 보인다.
        const shown = warpedShown
          ? []
          : surface.layers
              .filter((node) => node.id !== hiddenId)
              .map((node) => (live !== null && node.id === live.id ? live : node));
        const raster = renderComponentImpl({
          component: surface.component,
          layers: shown,
          materials,
          // 렌더러처럼 큰 쪽 축척으로 그리고, 캔버스 CSS 크기(축마다 따로)가 결과물처럼 늘인다.
          pxPerMm: rasterPxPerMmOf(view) * dpr,
          createContext,
          measure,
        });
        drawImpl(canvas, raster);
      } catch {
        // 재료가 아직 없으면 다음 렌더에서 그린다.
      }
    }
    if (guideRef.current !== null && guide !== null && selected !== null) {
      try {
        drawImpl(guideRef.current, guide);
      } catch {
        // 안내선 래스터가 아직 없으면 다음 렌더에서 그린다.
      }
    }
  }, [
    surfaces,
    live,
    materials,
    fit.k,
    fit.left,
    fit.top,
    createContext,
    measure,
    guide,
    selected,
    renderComponentImpl,
    drawImpl,
    warpedShown,
    hiddenId,
  ]);

  useEffect(() => {
    const canvas = warpedRef.current;
    if (canvas === null || warped === null) return;
    canvas.width = warped.width;
    canvas.height = warped.height;
    canvas.getContext('2d')?.drawImage(warped, 0, 0);
  }, [warped]);

  const mmIn = (view: FlatView, event: { clientX: number; clientY: number }) =>
    clientToComponentMm(view, hostRect(), { x: event.clientX, y: event.clientY });

  /**
   * 누른 자리를 고른다. 곡면 합성이 보이면 사진 원본 px 로 바꿔 역워프 히트. 아니면 편집 중인 영역부터 차례로
   * 레이어 히트를 보고, 레이어가 없으면 누른 자리가 든 영역(빈 곳)을 알린다.
   */
  const pickAt = (event: { clientX: number; clientY: number }): Picked => {
    if (warpedShown && onWarpedHit !== undefined) {
      const rect = hostRect();
      const k = space.widthPx / photoBox.width;
      return {
        nodeId: onWarpedHit({
          x: (event.clientX - rect.left - photoBox.left) * k,
          y: (event.clientY - rect.top - photoBox.top) * k,
        }),
        componentId: null,
      };
    }
    const ordered = [
      ...surfaces.filter((surface) => surface.component.id === activeComponentId),
      ...surfaces.filter((surface) => surface.component.id !== activeComponentId),
    ];
    for (const surface of ordered) {
      const hit = hitTestLayers(surface.layers, mmIn(viewOf(surface.component.id), event));
      // 그룹은 선택만 된다(스펙 §2-3). 자식은 그룹 로컬 좌표라 가장 바깥 그룹을 고른다.
      if (hit !== null)
        return { nodeId: hit.groupPath[0] ?? hit.nodeId, componentId: surface.component.id };
    }
    const under = ordered.find((surface) =>
      insideComponent(surface, mmIn(viewOf(surface.component.id), event)),
    );
    return { nodeId: null, componentId: under?.component.id ?? null };
  };

  const beginDrag = (
    kind: HandleKind,
    original: BoxNode,
    componentId: string,
    event: { clientX: number; clientY: number },
  ) => {
    setDrag({ kind, original, componentId, startMm: mmIn(viewOf(componentId), event) });
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // 글자를 바로 고치는 중에 바깥을 누르면 — 조합 중인 글자까지 먼저 올리고 고치기를 마친 뒤 누름을 처리한다.
    if (editing !== null) {
      flushInline.current?.();
      setEditing(null);
    }
    capture(event.pointerId);
    if (selected !== null && selectedView !== null) {
      const kind = hitHandle(selected.node, mmIn(selectedView, event), {
        x: selectedView.pxPerMmX,
        y: selectedView.pxPerMmY,
      });
      if (kind !== null) {
        beginDrag(kind, selected.node, selected.componentId, event);
        return;
      }
    }
    const picked = pickAt(event);
    if (picked.nodeId === null) {
      // 빈 곳 — 영역 확대 모드면 끌어서 옮길 수 있으니 클릭 처리는 뗄 때(안 움직였을 때) 한다.
      if (fitMode === 'area') {
        viewport.startPan(event, picked);
        return;
      }
      settleEmptyClick(picked);
      return;
    }
    if (picked.componentId !== null && picked.componentId !== activeComponentId)
      onActivate(picked.componentId);
    onSelect(picked.nodeId);
    const box = selectedBoxOf(surfaces, picked.nodeId);
    if (box !== null) beginDrag('move', box.node, box.componentId, event);
  };

  /** 빈 곳 클릭 — 다른 영역이면 그 영역으로 바꾸고, 선택은 푼다. */
  const settleEmptyClick = (picked: Picked) => {
    if (picked.componentId !== null && picked.componentId !== activeComponentId)
      onActivate(picked.componentId);
    onSelect(null);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (viewport.movePan(event)) return;
    if (drag === null) return;
    const current = mmIn(viewOf(drag.componentId), event);
    const { kind, original, startMm } = drag;
    if (kind === 'move') setLive(dragMove(original, startMm, current));
    else if (kind === 'rotate') setLive(dragRotate(original, current, event.shiftKey));
    else if (kind === 'side-l' || kind === 'side-r') {
      if (original.type === 'text') setLive(dragTextWidth(original, kind, current));
    } else if (isEdgeKind(kind)) {
      if (original.type === 'image') setLive(dragResizeEdge(original, kind, current));
      else if (kind === 'edge-t' || kind === 'edge-b')
        setLive(dragTextStretch(original, kind, current));
    } else if (original.type === 'text')
      setLive(dragTextCorner(original, kind, current, event.shiftKey)); // Shift = 폭·세로 늘림 따로
    else setLive(dragResize(original, kind, current, event.shiftKey)); // Shift = 비율 유지
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    release(event.pointerId);
    const panned = viewport.finishPan();
    if (panned !== null) {
      if (!panned.moved) settleEmptyClick(panned.payload);
      return;
    }
    if (live !== null) onChangeNode(live);
    setLive(null);
    setDrag(null);
  };

  const onPointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    release(event.pointerId);
    viewport.cancelPan();
    setLive(null);
    setDrag(null);
  };

  const onDoubleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (onTextInput === undefined) return;
    const picked = pickAt(event);
    if (picked.nodeId === null) return;
    const target = findNode(documentOf(surfaces), picked.nodeId);
    if (target?.node.type === 'text') openTextEditor(target.node.id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && selected?.node.type === 'text' && onTextInput !== undefined) {
      event.preventDefault();
      openTextEditor(selected.node.id);
      return;
    }
    handleCanvasKeyDown(event, {
      selectedNodeId,
      hasBox: selected !== null,
      onSelect,
      onDeleteSelected,
      onNudge,
    });
  };

  return (
    <div
      ref={hostRef}
      role="application"
      aria-label="디자인 편집 캔버스"
      data-testid="flat-canvas"
      data-mode="flat"
      data-active-component={activeComponentId}
      data-selected-x={selected?.node.x ?? ''}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: 키보드로 미세 이동·삭제하는 편집 표면이라 role=application 으로 포커스를 받는다.
      tabIndex={0}
      data-fit-mode={fitMode}
      className={cn(
        'relative size-full touch-none overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        fitMode === 'area' && (viewport.panMoved ? 'cursor-grabbing' : 'cursor-grab'),
      )}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
    >
      {backdrop !== null && (
        <img
          src={backdrop.url}
          alt=""
          aria-hidden
          draggable={false}
          data-testid="flat-backdrop"
          className="pointer-events-none absolute max-w-none select-none"
          style={photoBox}
          onError={onBackdropError}
        />
      )}
      {warped !== null && backdrop !== null && (
        <canvas
          ref={warpedRef}
          aria-hidden
          data-testid="warped-preview"
          className={cn('pointer-events-none absolute', !warpedShown && 'invisible')}
          style={photoBox}
        />
      )}
      {surfaces.map((surface) => {
        const view = viewOf(surface.component.id);
        const isActive = surface.component.id === activeComponentId;
        const box = surfaceBoxOf(view, surface.component);
        // 모양 있는 영역은 사각형 흰 상자 대신 모양대로 흰 바탕을 캔버스 아래에 깐다(사진 없을 때만).
        const shaped = surface.component.shape !== undefined;
        return (
          <Fragment key={surface.component.id}>
            {backdrop === null && shaped && (
              <AreaShapeOutline
                component={surface.component}
                view={view}
                active={isActive}
                variant="fill"
              />
            )}
            <canvas
              ref={(element) => {
                if (element === null) canvasRefs.current.delete(surface.component.id);
                else canvasRefs.current.set(surface.component.id, element);
              }}
              data-testid={`flat-surface-${surface.component.id}`}
              data-active={isActive || undefined}
              onPointerEnter={() => setHoveredId(surface.component.id)}
              onPointerLeave={() => setHoveredId((id) => (id === surface.component.id ? null : id))}
              data-warped={warpedShown ? 'shown' : undefined}
              className={cn('absolute', backdrop === null && !shaped && 'bg-background shadow-sm')}
              style={box}
            />
            <AreaShapeOutline
              component={surface.component}
              view={view}
              active={isActive}
              emphasized={
                hoveredId === surface.component.id ||
                (selected !== null && selected.componentId === surface.component.id)
              }
            />
          </Fragment>
        );
      })}
      {guide !== null && selected !== null && selectedView !== null && (
        <canvas
          ref={guideRef}
          className="pointer-events-none absolute opacity-70"
          style={surfaceBoxOf(
            selectedView,
            surfaceOf(selected.componentId)?.component ?? { widthMm: 0, heightMm: 0 },
          )}
        />
      )}
      {selected !== null && selectedView !== null && (
        <SelectionOverlay
          node={selected.node}
          view={selectedView}
          onPointerDownHandle={(kind, event) => {
            event.stopPropagation();
            capture(event.pointerId);
            beginDrag(kind, selected.node, selected.componentId, event);
          }}
          rotating={drag?.kind === 'rotate'}
        />
      )}
      {selected !== null &&
        selectedView !== null &&
        selected.node.type === 'text' &&
        drag === null &&
        editingText === null &&
        onTextInput !== undefined && <TextEditHint node={selected.node} view={selectedView} />}
      {selected !== null && selectedView !== null && drag?.kind === 'rotate' && (
        <RotationFeedback node={selected.node} view={selectedView} viewport={viewport.size} />
      )}
      {editing !== null && editingText !== null && selectedView !== null && (
        <InlineTextEditor
          key={editing.session}
          flushRef={flushInline}
          node={editingText}
          view={selectedView}
          materials={materials}
          onInput={(text) => onTextInput?.(editing.nodeId, text, editing.session)}
          onFinish={(reason) => {
            setEditing(null);
            if (reason === 'escape') hostRef.current?.focus({ preventScroll: true });
          }}
        />
      )}
    </div>
  );
};
